import React from 'react'
import { useMemo, useState } from 'react'
import {
	type ClassStudentAnalytics,
	type CreativeCanvasStationIssue,
	type CollaborativeCulminatingMomentAnalyticsScore,
	type CreativeCanvasStationCriterion,
	type CreativeCanvasGradingOption,
	didAnalyticsDocumentPass,
} from '@mission.io/mission-toolkit'
import {
	AttemptFlipper,
	Header,
	HeaderCard,
	StyledQuestionCard,
	StyledQuestionGrid,
} from './SharedComponents'
import { styled } from 'styled-components'
import { FaCheck, FaCircle } from 'react-icons/fa'
import { FaX } from 'react-icons/fa6'

const FEATURE_FLAG_SHOW_DOCUMENT_REVIEW = false // added here because there is no config.js, the document url in this file should point to the document's image before this flag is removed or set to true

export type CollaborativeCulminatingMomentDocumentScores = {|
	issue: CreativeCanvasStationIssue,
	scores: CollaborativeCulminatingMomentAnalyticsScore,
|}

export type CollaborativeCulminatingMomentDocumentData = {|
	documentId: string,
	collaborativeCulminatingMomentData: Array<CollaborativeCulminatingMomentDocumentScores>,
|}

/**
 * DocumentReview - Shows the creative canvas documents interacted with throughout the mission
 *
 * @param {Object} props - the react props
 * @param {?ClassStudentAnalytics} props.analytics - the analytics for the current mission
 *
 * @return {React$Node}
 */
export function DocumentReview({
	analytics,
}: {
	analytics: ?ClassStudentAnalytics,
}): React$Node {
	const documentData: Array<CollaborativeCulminatingMomentDocumentData> =
		useMemo(() => {
			const documentIdToCollaborativeCulminatingMomentData: {
				[documentId: string]: Array<CollaborativeCulminatingMomentDocumentScores>,
			} = {}

			Object.keys(analytics?.collaborativeCulminatingMoments ?? {}).forEach(
				collaborativeCulminatingMomentDataId => {
					const collaborativeCulminatingMomentData =
						analytics?.collaborativeCulminatingMoments?.[
							collaborativeCulminatingMomentDataId
						]
					if (!collaborativeCulminatingMomentData) {
						return
					}

					const issueIdToIssue = {}
					collaborativeCulminatingMomentData.issues.forEach(issue => {
						issueIdToIssue[issue.id] = issue
					})

					collaborativeCulminatingMomentData.documents.forEach(doc => {
						const documentIssue = issueIdToIssue[doc.issueId]
						if (!documentIssue) {
							return
						}
						documentIdToCollaborativeCulminatingMomentData[doc.documentId] ??=
							[]
						documentIdToCollaborativeCulminatingMomentData[doc.documentId].push(
							{
								issue: documentIssue,
								scores: doc.scores,
							}
						)
					})
				}
			)

			return Object.keys(documentIdToCollaborativeCulminatingMomentData).map(
				documentId => ({
					documentId,
					collaborativeCulminatingMomentData:
						documentIdToCollaborativeCulminatingMomentData[documentId],
				})
			)
		}, [analytics])

	if (!documentData || !FEATURE_FLAG_SHOW_DOCUMENT_REVIEW) {
		return null
	}

	return (
		<DocumentStyles>
			<Header>
				<HeaderCard>
					<h3 className="font-game-headline">Documents</h3>
				</HeaderCard>
			</Header>
			<StyledQuestionGrid>
				{documentData.map(data => (
					<DocumentFlipper data={data} key={data.documentId} />
				))}
			</StyledQuestionGrid>
		</DocumentStyles>
	)
}

/**
 * DocumentFlipper - A component for showing the attempts a student had on a document
 *
 * @param {Object} props - the react props
 * @param {CollaborativeCulminatingMomentDocumentData} props.data - the collaborative culminating moment data for a single document
 *
 * @return {React$Node}
 */
function DocumentFlipper({
	data,
}: {
	data: CollaborativeCulminatingMomentDocumentData,
}): React$Node {
	const [selectedIndex, setSelectedIndex] = useState(0)
	const selectedScoreData: ?CollaborativeCulminatingMomentDocumentScores =
		data.collaborativeCulminatingMomentData[selectedIndex]
	const documentPassed = selectedScoreData
		? didAnalyticsDocumentPass(
				selectedScoreData.scores,
				selectedScoreData.issue
		  )
		: false

	return (
		<Column>
			<Prompt>{selectedScoreData?.issue.prompt ?? ''}</Prompt>
			<Row>
				<CanvasWrapper>
					<CanvasPositioner className={documentPassed ? 'success' : 'failure'}>
						<DocumentImageBorder>
							<DocumentImage
								src={
									// TODO: replace with document image once that is completed
									'https://resources-cdn.mission.io/simulations/simulation-thumbnails/63ec78e43242ae001e2335e1-8hJl.jpg'
								}
							/>
						</DocumentImageBorder>
					</CanvasPositioner>
					<CanvasPassIndicatorPositioner className="canvas-pass-indicator">
						{documentPassed ? (
							<FaCheck className="icon radio-button success" />
						) : (
							<FaX className="icon radio-button failure" />
						)}
					</CanvasPassIndicatorPositioner>
				</CanvasWrapper>
				<CriteriaList>
					{selectedScoreData ? (
						<CriteriaScoreViewer scoreData={selectedScoreData} />
					) : null}
				</CriteriaList>
			</Row>
			<AttemptFlipper
				currentIndex={selectedIndex}
				length={data.collaborativeCulminatingMomentData.length}
				setCurrentIndex={setSelectedIndex}
			/>
		</Column>
	)
}

/**
 * CriteriaScoreViewer - a component which shows the criteria and how a student was scored on the criteria
 *
 * @param {Object} props - the react props
 * @param {CollaborativeCulminatingMomentDocumentScores} props.scoreData - the scores the student got
 *
 * @return {React$Node}
 */
function CriteriaScoreViewer({
	scoreData,
}: {
	scoreData: CollaborativeCulminatingMomentDocumentScores,
}): React$Node {
	return (
		<Items>
			{scoreData.issue.rubric.criteria.map(criteria => {
				const selectedOption = criteria.gradingOptions.find(
					option =>
						option.id ===
						scoreData.scores.criteriaId[criteria.id]?.selectedOption
				)
				const shouldShowOptions = !isDefaultYesNoCriteria(criteria)
				return (
					<Line
						key={criteria.id}
						className={
							selectedOption?.correct == null
								? 'unanswered'
								: selectedOption.correct
								? 'success'
								: 'failure'
						}>
						{selectedOption?.correct == null ? (
							<FaCircle className="icon radio-button unanswered" />
						) : selectedOption.correct ? (
							<FaCheck className="icon radio-button success" />
						) : (
							<FaX className="icon radio-button failure" />
						)}
						<CriteriaText>
							{criteria.text}
							{criteria.required ? <b> (Required)</b> : null}
						</CriteriaText>
						{shouldShowOptions ? (
							<Options
								selectedOption={selectedOption}
								allOptions={criteria.gradingOptions}
							/>
						) : null}
					</Line>
				)
			})}
		</Items>
	)
}

/**
 * Options - A react component which shows the selected grading option as well as shows all the grading options when hovered
 *
 * @param {Object} props - the react props
 * @param {?CreativeCanvasGradingOption} props.selectedOption - the selected grading option
 * @param {CreativeCanvasGradingOption[]} props.allOptions - all the possible grading options
 *
 * @return {React$Node}
 */
function Options({
	selectedOption,
	allOptions,
}: {
	selectedOption: ?CreativeCanvasGradingOption,
	allOptions: CreativeCanvasGradingOption[],
}): React$Node {
	const [areAllOptionsVisible, setAreAllOptionsVisible] = useState(false)

	return (
		<Option
			onMouseEnter={() => setAreAllOptionsVisible(true)}
			onMouseLeave={() => setAreAllOptionsVisible(false)}
			className={
				selectedOption == null
					? ''
					: selectedOption.correct
					? 'correct'
					: 'incorrect'
			}>
			{selectedOption?.text ?? 'Not Graded'}{' '}
			{areAllOptionsVisible ? (
				<AllOptionPositioner>
					{allOptions
						.sort(({ score: scoreA }, { score: scoreB }) => scoreB - scoreA)
						.map(option => (
							<AllOptionsOption
								key={option.id}
								className={option.correct ? 'correct' : 'incorrect'}>
								{option.text}
							</AllOptionsOption>
						))}
				</AllOptionPositioner>
			) : null}
		</Option>
	)
}

/**
 * isDefaultYesNoCriteria - check if the given criteria only has the default "yes" and "no" grading options
 *
 * @param {CreativeCanvasStationCriterion} criteria - the criteria to check
 *
 * @return {boolean}
 */
function isDefaultYesNoCriteria(
	criteria: CreativeCanvasStationCriterion
): boolean {
	if (criteria.gradingOptions.length !== 2) {
		return false
	}
	return (
		criteria.gradingOptions.some(
			option => option.text.toLowerCase() === 'yes' && option.correct
		) &&
		criteria.gradingOptions.some(
			option => option.text.toLowerCase() === 'no' && !option.correct
		)
	)
}

const DocumentStyles = styled.div`
	--font-size-m: ${({ theme }) => theme.fontm};
	--font-size-l: ${({ theme }) => theme.fontl};
	--font-size-xl: ${({ theme }) => theme.fontxl};

	--border-radius: ${({ theme }) => theme.spacing};
	--canvas-border-color: ${({ theme }) => theme.primaryDark};

	--spacing-half: ${({ theme }) => theme.spacingHalf};
	--spacing: ${({ theme }) => theme.spacing};
	--spacing2x: ${({ theme }) => theme.spacing2x};
	--spacing3x: ${({ theme }) => theme.spacing3x};

	--incorrect-color: ${({ theme }) => theme.error};
	--incorrect-text-color: ${({ theme }) => theme.neutralLightest};
	--incorrect-highlight: ${({ theme }) => theme.error}AA;

	--correct-color: ${({ theme }) => theme.success};
	--correct-text-color: ${({ theme }) => theme.neutralLightest};
	--correct-highlight: ${({ theme }) => theme.success}AA;

	--unanswered-color: ${({ theme }) => theme.neutral};
	--unanswered-text-color: ${({ theme }) => theme.neutral};
	--unanswered-highlight: ${({ theme }) => theme.neutral}AA;
`

const Prompt = styled.div`
	font-weight: bold;
	font-size: var(--font-size-m);
	margin-bottom: var(--spacing);
`

const CriteriaText = styled.div`
	flex: 1 1 0;
`

const Option = styled.div`
	border: 2px solid black;
	border-radius: var(--border-radius);
	&.correct {
		border-color: var(--correct-color);
	}
	&.incorrect {
		border-color: var(--incorrect-color);
	}
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 var(--spacing);
	position: relative;

	&:hover {
		cursor: pointer;
		background-color: #fff3;
	}
`

const AllOptionPositioner = styled.div`
	position: absolute;

	top: -2.5em;
	flex-wrap: none;
	text-wrap: none;
	display: flex;
	flex-direction: row;
	background-color: #364984cc;
	border-radius: var(--border-radius);
	border: 2px solid #fffa;
	padding: var(--spacing-half) var(--spacing);

	white-space: nowrap;
	gap: var(--spacing);

	pointer-events: none;
`

const AllOptionsOption = styled.div`
	border: 2px solid black;
	border-radius: var(--border-radius);
	&.correct {
		border-color: var(--correct-color);
		background-color: var(--correct-highlight);
	}
	&.incorrect {
		border-color: var(--incorrect-color);
		background-color: var(--incorrect-highlight);
	}
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 var(--spacing);
	white-space: no-wrap;
`

const Items = styled.div`
	padding-top: var(--spacing);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-half);
`

const Column = styled(StyledQuestionCard)`
	gap: var(--spacing);
`

const Row = styled.div`
	display: flex;
	flex-direction: row;
	gap: var(--spacing);

	.icon.success {
		background-color: var(--correct-color);
		color: var(--correct-text-color);
		padding: var(--spacing-half);
		font-size: var(--font-size-l);
	}
	.icon.failure {
		background-color: var(--incorrect-color);
		color: var(--incorrect-text-color);
		padding: var(--spacing-half);
		font-size: var(--font-size-l);
	}
	.icon.unanswered {
		background-color: var(--unanswered-color);
		color: var(--unanswered-text-color);
		padding: var(--spacing-half);
		font-size: var(--font-size-l);
	}
	.radio-button {
		border-radius: 50%;
		background-color: var(--correct-color);
		color: var(--correct-text-color);
		padding: var(--spacing-half);
		font-size: var(--font-size-l);
	}
`

const Line = styled.div`
	&.success {
		background-color: var(--correct-highlight);
	}
	&.failure {
		background-color: var(--incorrect-highlight);
	}
	&.unanswered {
		background-color: var(--unanswered-highlight);
	}
	border-radius: var(--spacing);
	text-align: left;
	padding: var(--spacing-half) var(--spacing);
	display: flex;
	flex-direction: row;
	align-items: center;

	gap: var(--spacing);
`

const CanvasWrapper = styled.div`
	flex: 1 1 0;
	position: relative;

	&:hover {
		.canvas-pass-indicator {
			opacity: 0;
		}
	}
`

const CriteriaList = styled.div`
	flex: 1 1 0;
	position: relative;
`

const CanvasPassIndicatorPositioner = styled.div`
	position: absolute;
	bottom: var(--spacing2x);
	right: var(--spacing3x);
	z-index: 1;
	font-size: 2em;
	opacity: 0.5;

	transition: opacity 0.5s;
`

const CanvasPositioner = styled.div`
	padding: var(--spacing);
	height: 100%;

	--canvas-border-radius: 16px;
	--canvas-inner-border-radius: 8px;
	border-radius: 24px;

	&.success {
		background-color: var(--correct-highlight);
	}
	&.failure {
		background-color: var(--incorrect-highlight);
	}
`

const DocumentImage = styled.img`
	max-height: 100%;
	max-width: 100%;
	border-radius: var(--canvas-inner-border-radius);
`

const DocumentImageBorder = styled.div`
	padding: var(--spacing);
	background-color: var(--canvas-border-color);
	border-radius: var(--canvas-border-radius);
	display: flex;
`
